import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Clock, User, Calendar as CalendarIcon, TrendingUp } from 'lucide-react';
import api from '../../../../services/api';
import LoadingSpinner from '../../../common/LoadingSpinner';

const DateSpecificAttendance = ({ basePath = '/admin/attendance' }) => {
  const { date } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [attendances, setAttendances] = useState([]);
  const [holidays, setHolidays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    total: 0,
    fullDay: 0,
    halfDay: 0,
    absent: 0,
    checkedIn: 0
  });

  const selectedStatus = new URLSearchParams(location.search).get('status');

  const handleStatusFilter = (status) => {
    const search = status ? `?status=${status}` : '';
    navigate(`${basePath}/date-wise/${date}${search}`);
  };

  const normalizeAttendanceStatus = (attendance) => {
    if (attendance?.status === 'full-day' || attendance?.status === 'half-day') {
      return attendance.status;
    }

    if (attendance?.checkInTime && !attendance?.checkOutTime) {
      return 'checked-in';
    }

    if (attendance?.checkInTime && attendance?.checkOutTime) {
      return attendance?.totalHours >= 8 ? 'full-day' : 'half-day';
    }

    return 'absent';
  };

  useEffect(() => {
    fetchDateAttendance();
    fetchHolidays();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load when route date changes
  }, [date]);

  const fetchDateAttendance = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/admin/attendance/date/${date}`);
      const data = response.data.attendances || [];
      setAttendances(data);

      // Calculate stats
      const statistics = {
        total: data.length,
        fullDay: data.filter((a) => normalizeAttendanceStatus(a) === 'full-day').length,
        halfDay: data.filter((a) => normalizeAttendanceStatus(a) === 'half-day').length,
        absent: data.filter((a) => normalizeAttendanceStatus(a) === 'absent').length,
        checkedIn: data.filter((a) => normalizeAttendanceStatus(a) === 'checked-in').length
      };
      setStats(statistics);
    } catch (error) {
      console.error('Error fetching date attendance:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchHolidays = async () => {
    try {
      const response = await api.get('/admin/holidays');
      setHolidays(response.data || []);
    } catch (error) {
      console.error('Error fetching holidays:', error);
    }
  };

  const isHoliday = () => {
    const currentDate = new Date(date);
    return holidays.find(h => {
      const holidayDate = new Date(h.date);
      // Compare year, month, and day separately to avoid timezone issues
      return holidayDate.getFullYear() === currentDate.getFullYear() &&
             holidayDate.getMonth() === currentDate.getMonth() &&
             holidayDate.getDate() === currentDate.getDate();
    });
  };

  const isSunday = () => {
    const currentDate = new Date(date);
    return currentDate.getDay() === 0;
  };

  const formatTime = (dateString) => {
    if (!dateString) return '--:--';
    return new Date(dateString).toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatTotalHours = (totalHours) => {
    if (!Number.isFinite(totalHours) || totalHours <= 0) {
      return '--';
    }

    const totalMinutes = Math.round(totalHours * 60);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours}hr ${minutes}min`;
    }
  
    if (hours > 0) {
      return `${hours}hr`;
    }
  
    return `${minutes}min`;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'full-day':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'half-day':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'absent':
        return 'bg-red-100 text-red-800 border-red-200';
      case 'checked-in':
        return 'bg-orange-100 text-orange-700 border-orange-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'full-day':
        return '✓';
      case 'half-day':
        return '½';
      case 'absent':
        return '✗';
      case 'checked-in':
        return '⏱';
      default:
        return '?';
    }
  };

  const selectedStatusLabelMap = {
    present: 'Present',
    'full-day': 'Full Day',
    'half-day': 'Half Day',
    absent: 'Absent',
    'checked-in': 'Checked In'
  };

  const visibleAttendances = attendances.filter((attendance) => {
    if (!attendance.userId) return false;
    if (!selectedStatus) return true;
    if (selectedStatus === 'present') {
      return normalizeAttendanceStatus(attendance) !== 'absent';
    }
    return normalizeAttendanceStatus(attendance) === selectedStatus;
  });

  const formattedDate = new Date(date).toLocaleDateString('en-US', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });

  const statCards = [
    {
      title: 'Total Records',
      value: stats.total,
      filter: null,
      active: !selectedStatus,
      borderColor: 'border-blue-500',
      activeRing: 'ring-blue-200',
      valueColor: 'text-gray-900',
      iconBg: 'bg-blue-100',
      icon: <User className="h-6 w-6 text-blue-600" />
    },
    {
      title: 'Full Day',
      value: stats.fullDay,
      filter: 'full-day',
      active: selectedStatus === 'full-day',
      borderColor: 'border-green-500',
      activeRing: 'ring-green-200',
      valueColor: 'text-green-600',
      iconBg: 'bg-green-100',
      icon: <TrendingUp className="h-6 w-6 text-green-600" />
    },
    {
      title: 'Half Day',
      value: stats.halfDay,
      filter: 'half-day',
      active: selectedStatus === 'half-day',
      borderColor: 'border-yellow-500',
      activeRing: 'ring-yellow-200',
      valueColor: 'text-yellow-600',
      iconBg: 'bg-yellow-100',
      icon: <Clock className="h-6 w-6 text-yellow-600" />
    },
    {
      title: 'Checked In',
      value: stats.checkedIn,
      filter: 'checked-in',
      active: selectedStatus === 'checked-in',
      borderColor: 'border-orange-500',
      activeRing: 'ring-orange-200',
      valueColor: 'text-orange-600',
      iconBg: 'bg-orange-100',
      icon: <Clock className="h-6 w-6 text-orange-600" />
    },
    {
      title: 'Absent',
      value: stats.absent,
      filter: 'absent',
      active: selectedStatus === 'absent',
      borderColor: 'border-red-500',
      activeRing: 'ring-red-200',
      valueColor: 'text-red-600',
      iconBg: 'bg-red-100',
      icon: <span className="text-2xl font-bold text-red-600">✗</span>
    }
  ];

  if (loading) {
    return <LoadingSpinner size="lg" className="mt-8" />;
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-2xl shadow-xl p-8 text-white">
        <button
          onClick={() => navigate(`${basePath}/date-wise`)}
          className="flex items-center space-x-2 text-blue-100 hover:text-white transition-colors mb-4 group"
        >
          <ArrowLeft className="h-5 w-5 group-hover:-translate-x-1 transition-transform" />
          <span className="font-medium">Back to Calendar</span>
        </button>
        
        <div className="flex items-center space-x-4 mb-2">
          <div className="bg-blue-400 bg-opacity-30 rounded-full p-3">
            <CalendarIcon className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-3xl font-bold">{formattedDate}</h1>
            <p className="text-blue-100 mt-1">Attendance Records</p>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {statCards.map((card) => (
          <button
            key={card.title}
            type="button"
            onClick={() => handleStatusFilter(card.filter)}
            className={`bg-white rounded-xl shadow-lg p-6 border-l-4 ${card.borderColor} text-left transition hover:-translate-y-0.5 hover:shadow-xl ${card.active ? `ring-4 ${card.activeRing}` : ''}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{card.title}</p>
                <p className={`text-3xl font-bold mt-1 ${card.valueColor}`}>{card.value}</p>
              </div>
              <div className={`${card.iconBg} rounded-full p-3`}>
                {card.icon}
              </div>
            </div>
          </button>
        ))}
      </div>

      {/* Attendance Table */}
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden">
        <div className="px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
          <h3 className="text-xl font-bold text-gray-900">Employee Attendance Details</h3>
          <p className="text-sm text-gray-600 mt-1">Detailed breakdown of all attendance records for this date</p>
          {selectedStatus && selectedStatusLabelMap[selectedStatus] && (
            <div className="mt-3 inline-flex items-center rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-800">
              Filter: {selectedStatusLabelMap[selectedStatus]}
            </div>
          )}
        </div>
        
        {visibleAttendances.length === 0 ? (
          <div className="p-12 text-center">
            {isHoliday() ? (
              <div className="bg-sky-100 border-2 border-sky-300 rounded-xl p-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-sky-200 mb-4">
                  <CalendarIcon className="h-8 w-8 text-sky-700" />
                </div>
                <h3 className="text-xl font-bold text-sky-900 mb-2">{isHoliday().description}</h3>
                <p className="text-sky-700 font-medium">This is a holiday. No attendance records available.</p>
              </div>
            ) : isSunday() ? (
              <div className="bg-green-100 border-2 border-green-300 rounded-xl p-8">
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-green-200 mb-4">
                  <CalendarIcon className="h-8 w-8 text-green-700" />
                </div>
                <h3 className="text-xl font-bold text-green-900 mb-2">Full Day</h3>
                <p className="text-green-700 font-medium">Sunday is counted as a Full Day for all employees.</p>
              </div>
            ) : (
              <div>
                <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-100 mb-4">
                  <CalendarIcon className="h-8 w-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-1">No Attendance Records</h3>
                <p className="text-gray-500">
                  {selectedStatus
                    ? `No employees found for ${selectedStatusLabelMap[selectedStatus] || selectedStatus} on this date.`
                    : 'There are no attendance records for this date.'}
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    S.No.
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Employee
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Check In
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Check Out
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Total Hours
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {visibleAttendances.map((attendance, index) => {
                  const displayStatus = normalizeAttendanceStatus(attendance);
                  return (
                  <tr 
                    key={index} 
                    className="hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-700">
                      {index + 1}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center text-white font-bold text-lg shadow-md">
                            {attendance.userId?.name?.charAt(0).toUpperCase() || '?'}
                          </div>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-bold text-gray-900">
                            {attendance.userId?.name || 'Unknown User'}
                          </div>
                          <div className="text-sm text-gray-500">
                            {attendance.userId?.email || 'No email'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {formatTime(attendance.checkInTime)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <Clock className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-medium text-gray-900">
                          {formatTime(attendance.checkOutTime)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2">
                        <TrendingUp className="h-4 w-4 text-gray-400" />
                        <span className="text-sm font-bold text-gray-900">
                          {formatTotalHours(attendance.totalHours)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-3 py-1.5 rounded-full text-xs font-bold border-2 ${getStatusColor(displayStatus)}`}>
                        <span className="mr-1.5 text-lg">{getStatusIcon(displayStatus)}</span>
                        {(selectedStatusLabelMap[displayStatus] || displayStatus).toUpperCase()}
                        {attendance.isSynthesizedWfh ? ' · WFH' : ''}
                        {attendance.isSynthesizedSunday ? ' · Sunday' : ''}
                      </span>
                    </td>
                  </tr>
                );})}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Summary Footer */}
      {visibleAttendances.length > 0 && (
        <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl p-6 border border-blue-100">
          <div className="flex items-start space-x-4">
            <div className="flex-shrink-0">
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <svg className="w-5 h-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
              </div>
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-1">Attendance Summary</h3>
              <p className="text-gray-700">
                Out of {stats.total} total records, <span className="font-bold text-green-600">{stats.fullDay} full day</span>, 
                <span className="font-bold text-yellow-600"> {stats.halfDay} half day</span>, and 
                <span className="font-bold text-red-600"> {stats.absent} absent</span> entries were recorded for this date.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DateSpecificAttendance;
